import { Box, Button, CircularProgress, Tooltip } from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { useRecoilState } from 'recoil';
import CompactToggle from '~/components/toolbar/shared/CompactToggle';
import RegionFromRois from './RegionFromRois';
import { useAutoRegistration } from '~/hooks/useAutoRegistration';
import { voiCheckboxState } from '~/state/atoms/toolsAtoms';

const pillSx = {
    minWidth: 'auto',
    px: 1.5,
    py: 0.25,
    fontSize: 11,
    textTransform: 'none',
    borderRadius: 100,
    whiteSpace: 'nowrap',
} as const;

interface AutoRegisterOptionsProps {
    isDisabled: boolean;
    /** Why the run control is unavailable; shown on hover when disabled. */
    disabledReason?: string;
}

export default function AutoRegisterOptions({
    isDisabled,
    disabledReason,
}: AutoRegisterOptionsProps) {
    const { startProcessing, cancelProcessing, isSubmitting } =
        useAutoRegistration();
    const [voiCheckbox, setVoiCheckbox] = useRecoilState(voiCheckboxState);

    return (
        <>
            <CompactToggle
                labelId="voiSelection"
                tooltipId="voiSelectionDescription"
                value={voiCheckbox}
                onChange={setVoiCheckbox}
            />
            {voiCheckbox && <RegionFromRois />}
            {isSubmitting ? (
                <Button
                    variant="outlined"
                    color="warning"
                    size="small"
                    onClick={cancelProcessing}
                    startIcon={<CircularProgress size={12} color="inherit" />}
                    sx={pillSx}
                >
                    <FormattedMessage id="cancel" />
                </Button>
            ) : (
                <Tooltip title={isDisabled ? (disabledReason ?? '') : ''}>
                    {/* Wrapper needed for MUI Tooltip to work on disabled buttons */}
                    <Box component="span">
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={startProcessing}
                            disabled={isDisabled}
                            data-testid="auto-register-run-button"
                            sx={pillSx}
                        >
                            <FormattedMessage id="workflow.imageRegistration.autoRegister" />
                        </Button>
                    </Box>
                </Tooltip>
            )}
        </>
    );
}
